import React, { useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tab,
  Tabs,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import sh from "bundles/common/utils/sh";
import {
  dialogLabelSx,
  dialogInputSx,
  ghostButtonSx,
  primaryButtonSx,
} from "bundles/Internal/components/common/FloatingTimer";

export default function CreateProjectDialog({ open, onClose, authenticity_token, onCreated }) {
  const [newProjectTab, setNewProjectTab] = useState(0);
  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectCompany, setNewProjectCompany] = useState(null);
  const [companySearch, setCompanySearch] = useState("");
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [companySuggestionsOpen, setCompanySuggestionsOpen] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const companyDebounceRef = useRef(null);
  const [pipelineSearch, setPipelineSearch] = useState("");
  const [pipelineResults, setPipelineResults] = useState([]);
  const [pipelineSuggestionsOpen, setPipelineSuggestionsOpen] = useState(false);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [loadingPipelines, setLoadingPipelines] = useState(false);
  const pipelineDebounceRef = useRef(null);

  const reset = () => {
    setNewProjectTab(0);
    setNewProjectName("");
    setNewProjectCompany(null);
    setCompanySearch("");
    setCompanySuggestions([]);
    setCompanySuggestionsOpen(false);
    clearTimeout(companyDebounceRef.current);
    setPipelineSearch("");
    setPipelineResults([]);
    setPipelineSuggestionsOpen(false);
    setSelectedPipeline(null);
    clearTimeout(pipelineDebounceRef.current);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const searchCompanies = (q) => {
    if (!q || q.trim().length < 2) {
      setCompanySuggestions([]);
      return;
    }
    setLoadingCompanies(true);
    sh.get("/internal/search/companies", { params: { q: q.trim() } })
      .then((res) => setCompanySuggestions(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCompanySuggestions([]))
      .finally(() => setLoadingCompanies(false));
  };

  const searchPipelines = (q) => {
    if (!q || q.trim().length < 2) {
      setPipelineResults([]);
      return;
    }
    setLoadingPipelines(true);
    sh.get("/internal/search/profile_pipelines", { params: { q: q.trim() } })
      .then((res) => setPipelineResults(Array.isArray(res.data) ? res.data : []))
      .catch(() => setPipelineResults([]))
      .finally(() => setLoadingPipelines(false));
  };

  const handleCreate = () => {
    const finalName = newProjectName.trim() || (newProjectTab === 0 ? selectedPipeline?.title : newProjectCompany?.name);
    if (!finalName) return;
    const payload = { name: finalName };
    if (newProjectTab === 0) {
      if (selectedPipeline) payload.profile_pipeline_id = selectedPipeline.id;
    } else {
      if (newProjectCompany) payload.company_id = newProjectCompany.id;
    }
    sh.post("/internal/time_projects", {
      authenticity_token,
      time_project: payload,
    }).then((res) => {
      onCreated(res.data);
      handleClose();
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{ sx: { borderRadius: "16px", minWidth: 360, maxWidth: 400, p: 0 } }}
    >
      <DialogTitle
        sx={{
          fontSize: 16,
          fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
          color: "hsl(150 30% 12%)",
          pb: 0,
          pt: 2,
          px: 2,
        }}
      >
        Create project
      </DialogTitle>
      <Tabs
        value={newProjectTab}
        onChange={(_, v) => setNewProjectTab(v)}
        sx={{
          minHeight: 36,
          px: 2,
          "& .MuiTabs-indicator": { backgroundColor: "#3b82f6" },
        }}
      >
        <Tab
          label="Pipeline"
          sx={{ minHeight: 36, fontSize: 13, textTransform: "none", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", p: "6px 12px" }}
        />
        <Tab
          label="Company"
          sx={{ minHeight: 36, fontSize: 13, textTransform: "none", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", p: "6px 12px" }}
        />
      </Tabs>
      <DialogContent sx={{ pt: 1.5, px: 2, pb: 1, display: "flex", flexDirection: "column", gap: 2 }}>
        {newProjectTab === 0 ? (
          /* Pipeline Search */
          <Box>
            <Typography sx={dialogLabelSx}>Pipeline</Typography>
            <Box sx={{ position: "relative" }}>
              {selectedPipeline ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    width: "100%",
                    px: 1.5,
                    py: 0.75,
                    borderRadius: "12px",
                    border: "1px solid hsla(150, 38%, 17%, 0.25)",
                    backgroundColor: "hsla(150, 38%, 17%, 0.06)",
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)", lineHeight: 1.3 }} noWrap>
                      {selectedPipeline.title}
                    </Typography>
                    <Typography sx={{ fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)", lineHeight: 1.3, textTransform: "capitalize" }}>
                      {selectedPipeline.pipeline_type}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    aria-label="Clear pipeline"
                    onClick={() => {
                      setSelectedPipeline(null);
                      setPipelineSearch("");
                      setPipelineResults([]);
                    }}
                    sx={{ p: 0.25, "&:hover": { backgroundColor: "hsl(140 8% 90%)" } }}
                  >
                    <Close sx={{ fontSize: 14, color: "hsl(150 10% 46%)" }} />
                  </IconButton>
                </Box>
              ) : (
                <Box
                  component="input"
                  placeholder="Search pipeline..."
                  value={pipelineSearch}
                  onChange={(e) => {
                    const val = e.target.value;
                    setPipelineSearch(val);
                    setPipelineSuggestionsOpen(true);
                    clearTimeout(pipelineDebounceRef.current);
                    pipelineDebounceRef.current = setTimeout(() => searchPipelines(val), 250);
                  }}
                  onFocus={() => pipelineSearch.trim().length >= 2 && setPipelineSuggestionsOpen(true)}
                  onBlur={() => setTimeout(() => setPipelineSuggestionsOpen(false), 150)}
                  autoFocus
                  sx={dialogInputSx}
                />
              )}

              {!selectedPipeline && pipelineSuggestionsOpen && pipelineSearch.trim().length >= 2 && (pipelineResults.length > 0 || loadingPipelines) && (
                <Paper
                  elevation={0}
                  sx={{
                    mt: 0.75,
                    width: "100%",
                    borderRadius: "12px",
                    border: "1px solid hsl(140 8% 88%)",
                    overflow: "hidden",
                  }}
                >
                  {loadingPipelines && pipelineResults.length === 0 ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
                      <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)" }}>
                        Searching...
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
                      {pipelineResults.map((p) => (
                        <Box
                          key={p.id}
                          component="button"
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setSelectedPipeline(p);
                            setPipelineSearch(p.title);
                            setPipelineResults([]);
                            setPipelineSuggestionsOpen(false);
                          }}
                          sx={{
                            width: "100%",
                            textAlign: "left",
                            border: "none",
                            backgroundColor: "transparent",
                            cursor: "pointer",
                            px: 1.5,
                            py: 1,
                            display: "block",
                            transition: "background-color 0.15s ease",
                            "&:hover": { backgroundColor: "hsla(140, 8%, 90%, 0.5)" },
                          }}
                        >
                          <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
                            {p.title}
                          </Typography>
                          <Typography sx={{ fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)", textTransform: "capitalize" }}>
                            {p.pipeline_type}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Paper>
              )}
            </Box>
          </Box>
        ) : (
          /* Company Search */
          <Box>
            <Typography sx={dialogLabelSx}>Company</Typography>
            <Box sx={{ position: "relative" }}>
              {newProjectCompany ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    width: "100%",
                    height: 40,
                    px: 1.5,
                    borderRadius: "12px",
                    border: "1px solid hsla(150, 38%, 17%, 0.25)",
                    backgroundColor: "hsla(150, 38%, 17%, 0.06)",
                  }}
                >
                  <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)" }} noWrap>
                    {newProjectCompany.name}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label="Clear company"
                    onClick={() => {
                      setNewProjectCompany(null);
                      setCompanySearch("");
                      setCompanySuggestions([]);
                    }}
                    sx={{ p: 0.25, "&:hover": { backgroundColor: "hsl(140 8% 90%)" } }}
                  >
                    <Close sx={{ fontSize: 14, color: "hsl(150 10% 46%)" }} />
                  </IconButton>
                </Box>
              ) : (
                <Box
                  component="input"
                  placeholder="Search company..."
                  value={companySearch}
                  onChange={(e) => {
                    const val = e.target.value;
                    setCompanySearch(val);
                    setCompanySuggestionsOpen(true);
                    clearTimeout(companyDebounceRef.current);
                    companyDebounceRef.current = setTimeout(() => searchCompanies(val), 250);
                  }}
                  onFocus={() => companySearch.trim().length >= 2 && setCompanySuggestionsOpen(true)}
                  onBlur={() => setTimeout(() => setCompanySuggestionsOpen(false), 150)}
                  autoFocus
                  sx={dialogInputSx}
                />
              )}

              {!newProjectCompany && companySuggestionsOpen && companySearch.trim().length >= 2 && (companySuggestions.length > 0 || loadingCompanies) && (
                <Paper
                  elevation={0}
                  sx={{
                    mt: 0.75,
                    width: "100%",
                    borderRadius: "12px",
                    border: "1px solid hsl(140 8% 88%)",
                    overflow: "hidden",
                  }}
                >
                  {loadingCompanies && companySuggestions.length === 0 ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
                      <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)" }}>
                        Searching...
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
                      {companySuggestions.map((c) => (
                        <Box
                          key={c.id}
                          component="button"
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setNewProjectCompany(c);
                            setCompanySearch(c.name);
                            setCompanySuggestions([]);
                            setCompanySuggestionsOpen(false);
                          }}
                          sx={{
                            width: "100%",
                            textAlign: "left",
                            border: "none",
                            backgroundColor: "transparent",
                            cursor: "pointer",
                            px: 1.5,
                            py: 1,
                            display: "block",
                            transition: "background-color 0.15s ease",
                            "&:hover": { backgroundColor: "hsla(140, 8%, 90%, 0.5)" },
                          }}
                        >
                          <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
                            {c.name}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Paper>
              )}
            </Box>
          </Box>
        )}

        <Box>
          <Typography sx={dialogLabelSx}>
            Project name <Box component="span" sx={{ fontFamily: "Segoe UI Regular, sans-serif, system-ui", textTransform: "none", opacity: 0.7 }}>(optional)</Box>
          </Typography>
          <Box
            component="input"
            placeholder={
              newProjectTab === 0
                ? selectedPipeline?.title ? `Defaults to "${selectedPipeline.title}"` : "e.g. Pipeline recruiting"
                : newProjectCompany?.name ? `Defaults to "${newProjectCompany.name}"` : "e.g. Internal recruiting"
            }
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (selectedPipeline?.id || newProjectCompany?.id || newProjectName.trim())) handleCreate();
            }}
            sx={dialogInputSx}
          />
          {newProjectName.trim() && (
            <Typography sx={{ mt: 0.75, fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)" }}>
              Will use <Box component="span" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>{newProjectName.trim()}</Box> as the project name
            </Typography>
          )}
          {!newProjectName.trim() && selectedPipeline?.title && (
            <Typography sx={{ mt: 0.75, fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)" }}>
              Will use <Box component="span" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>{selectedPipeline.title}</Box> as the project name
            </Typography>
          )}
          {!newProjectName.trim() && !selectedPipeline?.title && newProjectCompany?.name && (
            <Typography sx={{ mt: 0.75, fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)" }}>
              Will use <Box component="span" sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>{newProjectCompany.name}</Box> as the project name
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, pt: 1, gap: 1 }}>
        <Button onClick={handleClose} sx={ghostButtonSx}>
          Cancel
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disableElevation
          disabled={!newProjectName.trim() && !selectedPipeline?.title && !newProjectCompany?.name}
          sx={primaryButtonSx}
        >
          Create project
        </Button>
      </DialogActions>
    </Dialog>
  );
}
